import React, { useState, useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { LoginContext } from "../../../../../helpers/LoginContext";
import { apiPostRequest, apiGetRequest } from "../../../../../helpers/Requests";
import ModalSkeleton from "../../../../../components/Modals/ModalSkeleton";
import CustomButton from "../../../../../components/common/Button/CustomButton";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import MultiFileInput from "../../../../../components/common/FileUpload/MultiFileInput";
import MultiSelectDropdown from "../../../../../components/common/MultiSelectDropdown";
import SelectOption from "../../../../../components/common/Select/Select";
import DateRangeWithRegister from "../../../../../components/common/DateRangePicker/DateRangePickerWithRegister";
import { uploadFilesOnVapour } from "../../../../../Utils/uploadFileOnS3";
import {  URL_FEEDBACK_WORKER_LIST_FOR_BLACK_PORTAL } from "../../../../../store/services/URL";
import useCareLabels from "hooks/useCareLabels";
import CareLabelSkeleton from "components/common/CareLabelSkeleton/CareLabelSkeleton";

const statusOptions = [
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'neutral', label: 'Neutral' }
];

const CreateFeedbackModal = ({
  isOpen,
  onClose,
  onSuccess,
  managerList,
  participantsList,
  loadingManagers,
  loadingParticipants,
  selectedParticipantForm,
  setSelectedParticipantForm,
}) => {
  const { participantLabelPlural, isLoading: isCareLabelsLoading } = useCareLabels();
  const { isUserGracePeriodOver } = useContext(LoginContext);
  const [request, setRequest] = useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filesData, setFilesData] = useState([]);
  const [workersList, setWorkersList] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [selectedWorkerForm, setSelectedWorkerForm] = useState(null);
  const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      date: null,
      subject: "",
      attachments: [],
      participants: [],
      workers: [],
      email: [],
      details: "",
      status: statusFilter,
    },
  });

  const watchFormData = watch();

  useEffect(() => {
    if (isOpen) {
      fetchAllWorkersList();
    }
  }, [isOpen]);

  const fetchAllWorkersList = async () => {
    setLoadingWorkers(true);
    try {
      const response = await apiGetRequest(URL_FEEDBACK_WORKER_LIST_FOR_BLACK_PORTAL);
      if (response?.status === 200) {
        const formattedWorkers = response?.data?.data?.map((worker) => ({
          id: worker?.id,
          value: worker?.uuid || worker?.id,
          label: `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim() || worker?.name,
        }));
        setWorkersList(formattedWorkers || []);
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
      setWorkersList([]);
    } finally {
      setLoadingWorkers(false);
    }
  };


  const onSubmit = async (data) => {

    if (isUserGracePeriodOver || isSubmitting) return;

    setIsSubmitting(true);
    setRequest({ ...request, isRequested: true });

    try {
      let uploadedFiles = [];
      if (data?.attachments && data?.attachments?.length > 0) {
        uploadedFiles = await uploadFilesOnVapour(data?.attachments);
      }

      const managerEmails =
        data?.email?.map(item => {
        return {
          id: item?.id,
          name: item?.label,
          email: item?.value
        };
      }) || [];

      const participantData = data?.participants?.length > 0
        ? data?.participants?.map(participant => {
            const originalParticipant = participantsList.find(p => p.value === participant?.value || p.uuid === participant?.value);
            return {
              id: participant?.id || originalParticipant?.id,
              uuid: participant?.value || participant?.uuid,
              name: participant?.label || participant?.name
            };
          })
        : selectedParticipantForm?.value ? [{
            uuid: selectedParticipantForm.value,
            name: selectedParticipantForm.label
          }] : [];

      const workerData = data?.workers?.map(worker => {

        const originalWorker = workersList.find(w => w.value === worker?.value || w.uuid === worker?.value);
        return {
          id: worker?.id || originalWorker?.id,
          uuid: worker?.value || worker?.uuid,
          name: worker?.label || worker?.name
        };
      }) || [];

      const feedbackPayload = {
        date: data?.date,
        subject: data?.subject,
        email: managerEmails,
        details: data?.details,
        type: "feedback",
        attachments: uploadedFiles,
        participants: participantData,
        workers: workerData,
        feedback_assessment: data?.status?.value,
      };

      let res = await apiPostRequest("/hrm/feedback/store", feedbackPayload);

      if (res.status === 200) {
        reset({
          date: null,
          subject: "",
          email: [],
          details: "",
          attachments: [],
          participants: [],
          workers: [],
          feedback_assessment: statusFilter
        });
        setSelectedParticipantForm(null);
        setSelectedWorkerForm(null);
        setStatusFilter(statusFilter);
        setFilesData([]);
        toast.success("Feedback submitted successfully!");
        setRequest({ ...request, isRequested: false, isSuccess: true });
        onSuccess();
        onClose();
      }
    } catch (err) {
      setRequest({ ...request, isRequested: false, isFailed: true });
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset({
      date: null,
      subject: "",
      email: [],
      details: "",
      attachments: [],
      participants: [],
      workers: [],
      status: statusFilter
    });
    setSelectedParticipantForm(null);
    setSelectedWorkerForm(null);
    setStatusFilter(statusFilter);
    setFilesData([]);
    onClose();
  };

  const modalFooter = () => (
    <CustomButton
      type='submit'
      label='Submit'
      showLoading={request.isRequested || isSubmitting}
      isDisabled={request.isRequested || isSubmitting || isUserGracePeriodOver}
      clickHandler={() => {
        const buttonRef = document.getElementById("submitFeedback");
        if (buttonRef) {
          buttonRef.click();
        }
      }}
    />
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title='Add Feedback'
      cssClass='w-[60%]'
      closeModal={handleClose}
      modalFooter={modalFooter()}
    >
      <form onSubmit={handleSubmit(onSubmit)} className=''>
        <p className='text-sm text-gray-500 mb-4'>
          Please add your feedback details below
        </p>

        <div className='grid grid-cols-2 gap-x-4'>
          <div className='mt-1 col-span-2 sm:col-span-1'>
            <label className='did-floating-label'>
              {isCareLabelsLoading ? (
                <CareLabelSkeleton width="w-28" height="h-3.5" />
              ) : (
                participantLabelPlural
              )}
            </label>
             <MultiSelectDropdown
            showIcon
              data={participantsList}
              cssClass='z-30'
              handleChange={setSelectedParticipantForm}
            register={register}
              setValue={setValue}
              name='participants'
              watch={watch}
              control={control}
              isDisabled={loadingParticipants}
              className='w-full'
            />
          </div>

            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>Workers</label>
              <MultiSelectDropdown
                showIcon
                data={workersList}
                cssClass='z-30'
                name='workers'
                register={register}
                setValue={setValue}
                control={control}
                watch={watch}
                isDisabled={loadingWorkers}
                placeholder={loadingWorkers ? "Loading workers..." : "Select Workers"}
              />
            </div>

        <div className='mt-1 col-span-2 sm:col-span-1'>
            <label className='did-floating-label'>Mention Manager</label>
            <MultiSelectDropdown
              showIcon
              data={managerList}
              cssClass='z-30'
              name='email'
              register={register}
              setValue={setValue}
              control={control}
              watch={watch}
              isDisabled={loadingManagers}
              placeholder={loadingManagers ? "Loading managers..." : "Select Managers"}
            />
          </div>


        <div className='mt-1 col-span-2 sm:col-span-1'>
            <label className='did-floating-label'>Type
              <span className="text-red-500">*</span>
            </label>
            <Controller
              name='status'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectOption
                  cssClass='mt-1'
                  isSearch={false}
                  data={statusOptions}
                  selectedOption={field.value}
                  handleChange={(e) => {

                    setStatusFilter(e);
                    field.onChange(e);
                  }}
                  placeholder="Select Status"
                  className='w-full'
                />
              )}
            />

            {errors?.status?.type === "required" && (
              <span className='text-sm text-red-500'>
                This field is required!
              </span>
            )}
          </div>

           <div className='mt-1 col-span-2 sm:col-span-1'>
            <label className='did-floating-label'>Subject
                 <span className="text-red-500">*</span>
            </label>
            <input
              {...register("subject", { required: true })}
              className='did-floating-input'
              type='text'
            />
            {errors?.subject?.type === "required" && (
              <span className='text-sm text-red-500'>
                This field is required!
              </span>
            )}
          </div>


          <div className='mt-1 col-span-2 sm:col-span-1'>
            <label className='did-floating-label'>Date
                 <span className="text-red-500">*</span>
            </label>
           <Controller
                name='date'
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <DateRangeWithRegister
                    isSingleDate
                    field={field}
                    fieldState={fieldState}
                    placeholder='Select  Date'
                    showCalendarIcon
                  />
                )}
            />
            {errors?.date?.type === "required" && (
              <span className='text-sm text-red-500'>
                This field is required!
              </span>
            )}
          </div>



          <div className='mt-1 col-span-2'>
            <label className='did-floating-label'>Details
                 <span className="text-red-500">*</span>
            </label>
            <Controller
              name='details'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CKEditor
                  editor={ClassicEditor}
                  data={field.value || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    field.onChange(data);
                  }}
                  config={{
                    placeholder: "Enter feedback details here...",
                  }}
                />
              )}
            />
            {errors?.details?.type === "required" && (
              <span className='text-sm text-red-500'>
                This field is required!
              </span>
            )}
          </div>

          <div className='mt-4 col-span-2'>
            <label className='did-floating-label'>Attachments</label>
            <MultiFileInput
              filenameLimit={150}
              type='feedback'
              handleFile={(files) => setValue("attachments", files)}
              size={5}
              files={watchFormData?.attachments}
              handleDelete={(remainingFiles) => setValue("attachments", remainingFiles)}
              showDragAndDrop={false}
              isHardClearAllStates={isSubmitting === true}
              setFilesData={setFilesData}
              setIsSubmitting={setIsSubmitting}
              isFileUploadToS3={true}
            />
          </div>
        </div>
        <button
          type='submit'
          className='hidden sr-only'
          id='submitFeedback'
        />
      </form>
    </ModalSkeleton>
  );
};

export default CreateFeedbackModal;
